// ============================================================
// Workspace theme — shared vocabulary for the Square Code
// command-center workspace family (QUAM conversion).
//
// One accent system (Brand Guide v2 — same 8 accents the shell
// uses), one status vocabulary, one alpha-math helper. Every
// Workspace* component composes from here so tabs built on the
// family belong to the same system without being identical.
//
// Accents are literal hex (StatusBadge precedent) so per-client
// branding still flows through explicit `accent` props, and
// tint() accepts hex OR css variables via color-mix().
// ============================================================

export const WORKSPACE_ACCENTS = {
  orange: '#E8854A', // identity · primary
  cyan:   '#00aad2', // information · action
  green:  '#00a67d', // ready · healthy · live
  red:    '#e85d3a', // critical · blocked · urgent
  purple: '#7b5ea7', // intelligence · OLIVIUM · approvals
  gold:   '#c4a24e', // caution · pending · drafts
  blue:   '#5B9FD4', // infrastructure · building
  pink:   '#C97BAD', // creative
}

export const WORKSPACE_TEXT = {
  primary:   'var(--sc-text-primary, #f2f4ff)',
  secondary: 'var(--sc-text-secondary, #b4bfd8)',
  tertiary:  'var(--sc-text-tertiary, #8b93ad)',
  muted:     'var(--sc-text-muted, rgba(255,255,255,0.34))',
}

export const WORKSPACE_FONT = {
  mono: 'var(--sc-font-mono, "JetBrains Mono", ui-monospace, monospace)',
  ui:   'var(--sc-font-ui, "Instrument Sans", system-ui, sans-serif)',
}

export const WORKSPACE_SURFACE = {
  canvas:      'var(--sc-workspace-canvas, #0b0514)',
  planeBorder: 'var(--sc-workspace-plane-border, rgba(123,94,167,0.20))',
  card:        'var(--sc-workspace-card, rgba(255,255,255,0.02))',
  cardRaised:  'var(--sc-workspace-card-raised, rgba(255,255,255,0.035))',
  line:        'var(--sc-workspace-line, rgba(255,255,255,0.07))',
  lineFaint:   'var(--sc-workspace-line-faint, rgba(255,255,255,0.045))',
}

// Alpha math over any color (hex or css var).
// tint('#00aad2', 12) → 12% accent over transparent.
export function tint(color, pct) {
  return `color-mix(in srgb, ${color} ${pct}%, transparent)`
}

// Subtle technical grid — the command-center substrate layer.
export const WORKSPACE_GRID_URL =
  'url("data:image/svg+xml;utf8,' +
  '<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2250%22 height=%2250%22>' +
  '<path d=%22M50 0H0v50%22 fill=%22none%22 stroke=%22%23ffffff%22 stroke-width=%220.4%22 stroke-opacity=%220.035%22/>' +
  '</svg>")'

// ------------------------------------------------------------
// Status vocabulary — the persistent state language required by
// the workspace visual contract. Honest by design: unknown
// statuses render as muted uppercase, never as fake "ready".
// ------------------------------------------------------------

const TONE = {
  green:  WORKSPACE_ACCENTS.green,
  gold:   WORKSPACE_ACCENTS.gold,
  red:    WORKSPACE_ACCENTS.red,
  blue:   WORKSPACE_ACCENTS.blue,
  cyan:   WORKSPACE_ACCENTS.cyan,
  purple: WORKSPACE_ACCENTS.purple,
  orange: WORKSPACE_ACCENTS.orange,
  muted:  'rgba(255,255,255,0.38)',
}

const STATUS_META = {
  // green — real, live, done
  ready:        { label: 'READY',         accent: TONE.green },
  powered:      { label: 'POWERED',       accent: TONE.green },
  live:         { label: 'LIVE',          accent: TONE.green, pulse: true },
  active:       { label: 'ACTIVE',        accent: TONE.green },
  connected:    { label: 'CONNECTED',     accent: TONE.green },
  healthy:      { label: 'HEALTHY',       accent: TONE.green },
  complete:     { label: 'COMPLETE',      accent: TONE.green },
  sealed:       { label: 'SEALED',        accent: TONE.green },
  submitted:    { label: 'SUBMITTED',     accent: TONE.green },
  resolved:     { label: 'RESOLVED',      accent: TONE.green },
  approved:     { label: 'APPROVED',      accent: TONE.green },
  verified:     { label: 'VERIFIED',      accent: TONE.green },
  pass:         { label: 'PASS',          accent: TONE.green },

  // blue — in motion
  building:     { label: 'BUILDING',      accent: TONE.blue, pulse: true },
  running:      { label: 'RUNNING',       accent: TONE.blue, pulse: true },
  working:      { label: 'WORKING',       accent: TONE.blue, pulse: true },
  syncing:      { label: 'SYNCING',       accent: TONE.blue, pulse: true },

  // cyan — informational
  info:         { label: 'INFO',          accent: TONE.cyan },
  new:          { label: 'NEW',           accent: TONE.cyan },
  open:         { label: 'OPEN',          accent: TONE.cyan },

  // gold — honest not-yet states
  draft:        { label: 'DRAFT',         accent: TONE.gold },
  draft_only:   { label: 'DRAFT-ONLY',    accent: TONE.gold },
  prepared:     { label: 'PREPARED',      accent: TONE.gold },
  pending:      { label: 'PENDING',       accent: TONE.gold },
  queued:       { label: 'QUEUED',        accent: TONE.gold },
  review:       { label: 'REVIEW',        accent: TONE.gold },
  waiting:      { label: 'WAITING',       accent: TONE.gold },
  paused:       { label: 'PAUSED',        accent: TONE.gold },
  warn:         { label: 'WARN',          accent: TONE.gold },
  warning:      { label: 'WARNING',       accent: TONE.gold },
  attention:    { label: 'ATTENTION',     accent: TONE.gold },
  requires_setup: { label: 'REQUIRES SETUP', accent: TONE.gold },
  storage_pending: { label: 'STORAGE PENDING', accent: TONE.gold },

  // purple — governed gates
  approval_required: { label: 'APPROVAL REQUIRED', accent: TONE.purple },
  governed:     { label: 'GOVERNED',      accent: TONE.purple },

  // red — stop states
  blocked:      { label: 'BLOCKED',       accent: TONE.red },
  urgent:       { label: 'URGENT',        accent: TONE.red },
  critical:     { label: 'CRITICAL',      accent: TONE.red },
  error:        { label: 'ERROR',         accent: TONE.red },
  fail:         { label: 'FAIL',          accent: TONE.red },
  failed:       { label: 'FAILED',        accent: TONE.red },
  revoked:      { label: 'REVOKED',       accent: TONE.red },
  expired:      { label: 'EXPIRED',       accent: TONE.red },

  // muted — dormant / deferred
  idle:         { label: 'IDLE',          accent: TONE.muted },
  dormant:      { label: 'DORMANT',       accent: TONE.muted },
  archived:     { label: 'ARCHIVED',      accent: TONE.muted },
  offline:      { label: 'OFFLINE',       accent: TONE.muted },
  closed:       { label: 'CLOSED',        accent: TONE.muted },
  coming_later: { label: 'COMING LATER',  accent: TONE.muted },
  later_phase:  { label: 'LATER PHASE',   accent: TONE.muted },
  not_configured: { label: 'NOT CONFIGURED', accent: TONE.muted },
}

export function workspaceStatusMeta(status) {
  const key = String(status ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
  if (STATUS_META[key]) return STATUS_META[key]
  const fallback = key ? key.replace(/_/g, ' ').toUpperCase() : 'UNKNOWN'
  return { label: fallback, accent: TONE.muted }
}
